import React, { useEffect, useState } from 'react'
import { CustomContentModal } from '../../../../components/CustomContentModal'
import Button from '../../../../components/Form/Button'
import Input from '../../../../components/Form/Input'
import { formatUTCDate } from '../../../../util/string'
import { isEndDateTodayOrPast, todayIsoDate } from './ServiceAgreementCloseModal'

export interface ServiceAgreementReactivateRequest {
  meterServiceId: string
  serviceAgreementId?: string
  existingStartDate?: string | null
  existingEndDate?: string | null
}

export interface ServiceAgreementReactivateChoices {
  startDate: string
  clearEndDate: boolean
}

interface ServiceAgreementReactivateModalProps {
  isOpen: boolean
  request: ServiceAgreementReactivateRequest | null
  isSubmitting?: boolean
  onClose: () => void
  onConfirm: (choices: ServiceAgreementReactivateChoices) => void | Promise<void>
}

const isValidIsoDate = (value: string): boolean => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false
  const [year, month, day] = value.split('-').map(Number)
  const parsed = new Date(Date.UTC(year, month - 1, day))
  return (
    parsed.getUTCFullYear() === year && parsed.getUTCMonth() === month - 1 && parsed.getUTCDate() === day
  )
}

const formatExistingDate = (isoDate: string | null | undefined): string => {
  if (isoDate == null || isoDate === '') return 'not set'
  return formatUTCDate(isoDate) || isoDate
}

export const shouldClearEndDateOnReactivate = (
  existingEndDate: string | null | undefined,
  newStartDate: string
): boolean => {
  if (existingEndDate == null || existingEndDate === '') return false
  if (isEndDateTodayOrPast(existingEndDate)) return true
  return existingEndDate < newStartDate
}

const ServiceAgreementReactivateModal: React.FC<ServiceAgreementReactivateModalProps> = ({
  isOpen,
  request,
  isSubmitting = false,
  onClose,
  onConfirm,
}) => {
  const [startDate, setStartDate] = useState('')

  useEffect(() => {
    if (!isOpen || request == null) return
    setStartDate(todayIsoDate())
  }, [isOpen, request])

  if (!isOpen || request == null) return null

  const startDateValid = isValidIsoDate(startDate)
  const existingStartDate = request.existingStartDate
  const existingEndDate = request.existingEndDate
  const hasPreviousStart = existingStartDate != null && existingStartDate !== ''
  const hasPreviousEnd = existingEndDate != null && existingEndDate !== ''
  const clearEndDate = shouldClearEndDateOnReactivate(existingEndDate, startDate)
  const saLabel = request.serviceAgreementId ? `SA ${request.serviceAgreementId}` : 'this service agreement'

  return (
    <CustomContentModal isOpen={isOpen} onClose={isSubmitting ? () => undefined : onClose}>
      <div className="w-full max-w-md rounded-lg bg-white p-6 text-left shadow-xl dark:bg-slate-800">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">Reactivate service agreement?</h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          You are reactivating {saLabel}. Choose a start date.
        </p>

        <div className="mt-4">
          <Input
            type="date"
            label="Start date"
            value={startDate}
            disabled={isSubmitting}
            required
            error={!startDateValid ? 'Must be YYYY-MM-DD' : undefined}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>

        {hasPreviousStart || hasPreviousEnd ? (
          <p className="mt-4 text-sm text-amber-800 dark:text-amber-200">
            This action will affect the start date and end date which were previously set (start:{' '}
            {formatExistingDate(existingStartDate)}; end: {formatExistingDate(existingEndDate)}).
          </p>
        ) : null}

        <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button type="button" color="slate" disabled={isSubmitting} onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={!startDateValid || isSubmitting}
            onClick={() =>
              void onConfirm({
                startDate,
                clearEndDate,
              })
            }
          >
            {isSubmitting ? 'Saving…' : 'Confirm'}
          </Button>
        </div>
      </div>
    </CustomContentModal>
  )
}

export default ServiceAgreementReactivateModal
